import supabase from '../supabaseClient';

/**
 * Log an event to the academy_event_log table.
 * @param {Object} params - Event parameters
 * @param {string} params.event_type - Type of event (e.g. 'click', 'page_view', 'submit_assignment', etc.)
 * @param {string} [params.target_type] - What was acted on? (e.g. 'lesson', 'assignment', 'button')
 * @param {string} [params.target_id] - ID of the target (e.g. lesson_id, assignment_id, button_id)
 * @param {Object} [params.context] - Additional event data (will be stored as JSON)
 * @param {string} [params.session_id] - Session identifier (optional)
 * @param {string} [params.user_id] - User ID (optional; defaults to current user if available)
 * @param {string} [params.ip_address] - IP address (optional; backend only)
 * @param {string} [params.user_agent] - User agent string (optional; backend only)
 * @returns {Promise<{data, error}>}
 */
export async function logEvent({ event_type, target_type, target_id, context = {}, session_id, user_id, ip_address, user_agent }) {
  // Try to get user_id from Supabase auth if not provided
  let uid = user_id;
  if (!uid && typeof window !== 'undefined' && supabase.auth) {
    const user = await supabase.auth.getUser();
    uid = user?.data?.user?.id || null;
  }
  try {
    const res = await fetch('/api/analytics/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        event_type,
        target_type,
        target_id,
        context,
        session_id,
        user_id: uid,
        ip_address,
        user_agent,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { data: null, error: new Error(err.error || 'Failed to log event') };
    }
    const data = await res.json();
    return { data, error: null };
  } catch (e) {
    return { data: null, error: e };
  }
}

// Utility to fetch role counts (students, teachers, etc.)
export async function getRoleCounts() {
  const { data, error } = await supabase
    .from('academy_user_roles')
    .select('role_id, user_id, roles:academy_roles(role)');
  if (error) throw error;
  const counts = {};
  data.forEach(({ roles }) => {
    const role = roles?.role;
    if (role) counts[role] = (counts[role] || 0) + 1;
  });
  return counts;
}

// Utility to fetch total course count
export async function getCourseCount() {
  const { count } = await supabase
    .from('academy_courses')
    .select('id', { count: 'exact', head: true });
  return count;
}

// Utility to fetch average course rating
export async function getAverageCourseRating() {
  const { data, error } = await supabase
    .from('academy_course_ratings')
    .select('rating');
  if (error) throw error;
  if (!data.length) return null;
  const avg = data.reduce((sum, r) => sum + r.rating, 0) / data.length;
  return Number(avg.toFixed(2));
}

// Utility to fetch recent activity
export async function getRecentActivity(limit = 5) {
  const { data, error } = await supabase
    .from('academy_activity_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

// Utility to fetch assignment count
export async function getAssignmentCount() {
  const { count } = await supabase
    .from('academy_assignments')
    .select('id', { count: 'exact', head: true });
  return count;
}

// Utility to fetch assessment count
export async function getAssessmentCount() {
  const { count } = await supabase
    .from('academy_assessments')
    .select('id', { count: 'exact', head: true });
  return count;
}

// Utility to fetch enrollment count
export async function getEnrollmentCount() {
  const { count } = await supabase
    .from('academy_enrollments')
    .select('id', { count: 'exact', head: true });
  return count;
}

// Utility to fetch lesson view count
export async function getLessonViewCount() {
  const { count } = await supabase
    .from('academy_lesson_views')
    .select('id', { count: 'exact', head: true });
  return count;
}

// --- Analytics Filter Fetchers ---

// Fetch all student groups (optionally filtered by course_id)
export async function fetchStudentGroups({ course_id } = {}) {
  let query = supabase.from('academy_groups').select('*');
  if (course_id) query = query.eq('course_id', course_id);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// Fetch all sessions (optionally filtered by course_id, teacher_id, date range)
export async function fetchSessions({ course_id, teacher_id, start, end } = {}) {
  let query = supabase.from('academy_classroom_sessions').select('*');
  if (course_id) query = query.eq('course_id', course_id);
  if (teacher_id) query = query.eq('teacher_id', teacher_id);
  if (start) query = query.gte('scheduled_start', start);
  if (end) query = query.lte('scheduled_end', end);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// Fetch all teachers (optionally filtered by course_id)
export async function fetchTeachers({ course_id } = {}) {
  // Join academy_user_roles to roles
  let query = supabase
    .from('academy_user_roles')
    .select('user_id,academy_roles(role)');
  query = query.eq('academy_roles.role', 'teacher');
  if (course_id) query = query.eq('course_id', course_id);
  const { data, error } = await query;
  if (error) throw error;
  const userIds = data.map(r => r.user_id);
  if (!userIds.length) return [];
  // Fetch teacher details from profiles table
  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('id, email, display_name, name, avatar_url')
    .in('id', userIds);
  if (profileError) throw profileError;
  // Merge role info and profile info
  const profilesById = Object.fromEntries(profiles.map(p => [p.id, p]));
  return userIds.map(id => profilesById[id]).filter(Boolean);
}

// Fetch all assessments (optionally filtered by course_id)
export async function fetchAssessments({ course_id } = {}) {
  let query = supabase.from('academy_assessments').select('id, title, course_id');
  if (course_id) query = query.eq('course_id', course_id);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// --- Advanced Analytics ---

// Time series: generic function for any table/date_column
export async function getTimeSeries(table, dateColumn, { start, end, groupBy = 'day', filters = {} } = {}) {
  // groupBy: 'day', 'week', 'month'
  let fmt = groupBy === 'month' ? 'YYYY-MM' : groupBy === 'week' ? 'IYYY-IW' : 'YYYY-MM-DD';
  let query = supabase
    .from(table)
    .select(`${dateColumn}`);
  if (start) query = query.gte(dateColumn, start);
  if (end) query = query.lte(dateColumn, end);
  Object.entries(filters).forEach(([col, val]) => {
    if (val !== undefined) query = query.eq(col, val);
  });
  const { data, error } = await query;
  if (error) throw error;
  // Aggregate in JS (Supabase doesn't support group by date_trunc in JS client)
  const counts = {};
  data.forEach(row => {
    if (!row[dateColumn]) return;
    const d = new Date(row[dateColumn]);
    let key;
    if (groupBy === 'month') key = d.toISOString().slice(0,7);
    else if (groupBy === 'week') {
      // Get ISO week string
      const tmp = new Date(d.getTime());
      tmp.setDate(tmp.getDate() - ((tmp.getDay() + 6) % 7));
      key = tmp.getFullYear() + '-W' + String(Math.ceil((((d - new Date(tmp.getFullYear(),0,1)) / 86400000) + new Date(tmp.getFullYear(),0,1).getDay()+1)/7)).padStart(2,'0');
    } else key = d.toISOString().slice(0,10);
    counts[key] = (counts[key] || 0) + 1;
  });
  // Return as sorted array
  return Object.entries(counts).sort((a,b) => a[0].localeCompare(b[0])).map(([date, count]) => ({ date, count }));
}

// Engagement: active users per day
export async function getActiveUsersTimeSeries({ start, end, groupBy = 'day', role } = {}) {
  let query = supabase
    .from('academy_activity_log')
    .select('user_id, created_at');
  if (start) query = query.gte('created_at', start);
  if (end) query = query.lte('created_at', end);
  const { data, error } = await query;
  if (error) throw error;
  // Filter by role if needed
  let filtered = data;
  if (role) {
    const { data: userRoles } = await supabase
      .from('academy_user_roles')
      .select('user_id, role_id, roles:academy_roles(role)');
    const allowed = userRoles.filter(r => r.roles?.role === role).map(r => r.user_id);
    filtered = data.filter(row => allowed.includes(row.user_id));
  }
  const byDate = {};
  filtered.forEach(row => {
    const d = new Date(row.created_at).toISOString().slice(0,10);
    byDate[d] = byDate[d] || new Set();
    byDate[d].add(row.user_id);
  });
  return Object.entries(byDate).map(([date, set]) => ({ date, count: set.size }));
}

// Breakdown by course (enrollments, completions, etc.)
export async function getCourseBreakdown(type = 'enrollments') {
  let table, groupCol;
  if (type === 'enrollments') { table = 'academy_enrollments'; groupCol = 'course_id'; }
  else if (type === 'completions') { table = 'academy_enrollments'; groupCol = 'course_id'; }
  else if (type === 'submissions') { table = 'academy_submissions'; groupCol = 'assessment_id'; }
  else if (type === 'lesson_views') { table = 'academy_lesson_views'; groupCol = 'lesson_id'; }
  else throw new Error('Unknown type');
  const { data, error } = await supabase
    .from(table)
    .select(`${groupCol}`);
  if (error) throw error;
  const counts = {};
  data.forEach(row => {
    const k = row[groupCol];
    if (k) counts[k] = (counts[k] || 0) + 1;
  });
  return counts;
}

// Breakdown by assessment (submissions, average score)
export async function getAssessmentBreakdown() {
  const { data, error } = await supabase
    .from('academy_assessment_results')
    .select('assessment_id, score');
  if (error) throw error;
  const counts = {}, totals = {};
  data.forEach(row => {
    const k = row.assessment_id;
    if (k) {
      counts[k] = (counts[k] || 0) + 1;
      totals[k] = (totals[k] || 0) + (row.score || 0);
    }
  });
  return Object.entries(counts).map(([assessment_id, count]) => ({
    assessment_id,
    count,
    avgScore: Number((totals[assessment_id] / count).toFixed(2))
  }));
}

// Breakdown by role (activity, engagement)
export async function getRoleEngagement() {
  const { data: activity, error } = await supabase
    .from('academy_activity_log')
    .select('user_id');
  if (error) throw error;
  const { data: userRoles } = await supabase
    .from('academy_user_roles')
    .select('user_id, role_id, roles:academy_roles(role)');
  const roleMap = {};
  userRoles.forEach(r => {
    if (r.roles?.role) roleMap[r.user_id] = r.roles.role;
  });
  const counts = {};
  activity.forEach(row => {
    const role = roleMap[row.user_id];
    if (role) counts[role] = (counts[role] || 0) + 1;
  });
  return counts;
}

// --- Attendance and Time-on-Task (placeholders) ---
// These placeholder implementations return safe defaults so that UI can render without errors.
// Replace with real queries when the corresponding tables/metrics are finalized.

/**
 * Return a breakdown of attendance by session.
 * Expected shape: { [sessionId]: { present: number, absent: number, late: number } }
 */
export async function getSessionAttendanceBreakdown(/* filters */) {
  // TODO: Implement real query once attendance schema is finalized.
  return {};
}

/**
 * Return average time-on-task per session.
 * Expected shape: { [sessionId]: averageMinutes }
 */
export async function getSessionTimeOnTask(/* filters */) {
  // TODO: Implement real query once time-on-task metrics are available.
  return {};
}

/**
 * Return total and average time-on-task for the current user over the selected period.
 * Expected shape: { total: number, avg: number }
 */
export async function getUserTimeOnTask(/* filters */) {
  // TODO: Implement real query once time-on-task metrics are available.
  return { total: 0, avg: 0 };
}
